import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon, MinusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import checkoutService from '../../../api/checkoutService';
import {
  V2LineItem,
  CheckoutUpdateItemsPayload,
  V2ShippingMethod,
} from '../../../interface/checkoutInterface';
import { getCurrencyWithSymbol } from '../../../utils/formatPrice';
import { setIsCartLoading } from '../../../redux/slices/cartSlice';
import { setAvailableShippingMethods } from '../../../redux/slices/checkoutSlice';
import { setCartData } from '../../../redux/slices/cartSlice';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import { ICardFields } from '../../../interface/formInterface';
import { getFontSize } from '../../../utils/helper';
import { IProductModalProps } from '../../predefinedComponents/AddProductModal';
import './../../text-editor/style.scss';

interface CartItemProps extends ReduxDispatchProps, ReduxSelectorProps {
  product: V2LineItem;
  placeholder?: ICardFields;
  productSubtitle?: ICardFields;
  isSingleProduct?: boolean;
  productPricePosition: string;
  showDescription: boolean;
  colorCustomization: {
    price: string;
    productRemove: string;
    quantityIncreaseIcon: string;
    quantityDecreaseIcon: string;
    quantityDecreaseButton: string;
    quantityIncreaseButton: string;
    quantityBgColor: {
      color: string;
      isInherit: boolean;
    };
    quantityTextColor: string;
  };
  showQuantityButtons: boolean;
  cartCustomization: {
    hideImage: boolean;
    hideCloseButton: boolean;
    descriptionPosition: string;
  };
}

export const CartItem: React.FC<CartItemProps> = ({
  product,
  isSingleProduct,
  placeholder,
  colorCustomization,
  productSubtitle,
  productPricePosition,
  showDescription,
  showQuantityButtons,
  cartCustomization,
  useAppDispatch,
  useAppSelector,
}) => {
  const dispatch = useAppDispatch();
  const storeData = useAppSelector(state => state.store.storeData);
  const { cartData } = useAppSelector(state => state.cart);
  const [currentSelectedProduct, setCurrentSelectedProduct] =
    useState<IProductModalProps | null>(null);
  const funnelishProducts = useAppSelector(
    state => state.cart?.funnelishProducts,
  );

  useEffect(() => {
    if (funnelishProducts?.length) {
      const currentSelectedProduct = funnelishProducts.find(item => {
        return product.VariantID.includes(`${item.productDetails.variantid}`);
      });
      if (currentSelectedProduct) {
        setCurrentSelectedProduct(currentSelectedProduct);
      }
    }
  }, [funnelishProducts, cartData?.LineItems]);
  const { pageId } = useAppSelector(state => state.checkout);

  const updateCartItems = useCallback(
    async (quantity: number) => {
      const lineItems = [
        ...(cartData?.LineItems ?? []).map((item: V2LineItem) => ({
          ID: item.ID,
          Quantity: item.Quantity,
          VariantID: item.VariantID,
        })),
      ];
      const payload = {
        ID: cartData?.ID,
        LineItems: lineItems.map(item => {
          if (item.ID === product.ID) {
            return {
              ...item,
              Quantity: quantity,
            };
          }
          return item;
        }, []),
        ShopID: storeData?.ShopID,
        StoreID: Number(storeData?.StoreID),
        PageID: String(pageId) || '',
      };
      try {
        dispatch(setIsCartLoading(true));
        const res = await checkoutService.checkoutUpdateItems(
          payload as CheckoutUpdateItemsPayload,
          cartData || null,
        );
        dispatch(setCartData(res?.data?.Cart));
        dispatch(
          setAvailableShippingMethods(
            res?.data?.Cart?.AvailableShippingMethods as V2ShippingMethod[],
          ),
        );
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setIsCartLoading(false));
      }
    },
    [cartData, product.ID, storeData?.ShopID, storeData?.StoreID, dispatch],
  );

  const handleDecrease = async () => {
    await updateCartItems(product.Quantity - 1);
  };

  const handleIncrease = async () => {
    await updateCartItems(product.Quantity + 1);
  };

  const handleRemove = async () => {
    await updateCartItems(0);
  };
  const mandatoryProduct = useAppSelector(
    state => state.common.mandatoryProduct,
  );
  const isMandatoryProduct = mandatoryProduct
    ? mandatoryProduct === product?.VariantID
    : false;

  const dynamicProductsVarientIDToAdd = ['44273618485445'];

  const currentVariantID = `${product?.VariantID}`;
  const nonRemovableProducts = dynamicProductsVarientIDToAdd.map(
    id => `gid://shopify/ProductVariant/${id}`,
  );
  const isNonRemovable = nonRemovableProducts.includes(currentVariantID);

  return (
    <li
      key={product.ProductID}
      className={`flex ${(!showDescription || cartCustomization?.descriptionPosition === 'right') && !showQuantityButtons ? 'items-center' : 'items-start'} space-x-4 py-6`}>
      <div style={{ position: 'relative' }}>
        {!cartCustomization?.hideImage && (
          <>
            {currentSelectedProduct?.addAlias ? (
              <img
                width={500}
                height={500}
                src={
                  process.env.REACT_APP_S3_BUCKET_URL +
                  currentSelectedProduct?.aliasImageUrl
                }
                alt={currentSelectedProduct?.aliasName}
                style={{ maxWidth: '5rem' }}
                className="w-20 flex-none rounded-md object-cover object-center aspect-square"
                decoding="async"
              />
            ) : (
              <img
                width={500}
                height={500}
                src={product.ImageURL}
                alt={product.Name}
                style={{ maxWidth: '5rem' }}
                className="w-20 flex-none rounded-md object-cover object-center aspect-square"
                decoding="async"
              />
            )}
          </>
        )}
        {!cartCustomization?.hideImage && (
          <div
            className={`absolute top-0 right-0 min-w-5 min-h-5 px-1 py-0.5 rounded-full flex items-center justify-center text-xs -mt-2 -mr-2`}
            style={{
              backgroundColor: colorCustomization?.quantityBgColor?.isInherit
                ? 'inherit'
                : colorCustomization?.quantityBgColor?.color,
              color: colorCustomization?.quantityTextColor,
            }}>
            {product.Quantity}
          </div>
        )}
      </div>
      <div className="flex-auto space-y-1">
        <div className="flex items-center gap-2">
          {cartCustomization?.hideImage && (
            <span
              className="min-w-5 min-h-5 px-1 py-0.5 rounded-full flex items-center justify-center text-xs"
              style={{
                backgroundColor: colorCustomization?.quantityBgColor?.isInherit
                  ? 'inherit'
                  : colorCustomization?.quantityBgColor?.color,
                color: colorCustomization?.quantityTextColor,
              }}>
              {product.Quantity}
            </span>
          )}
          <p className="productNameStyle">{product.Name}</p>
        </div>
        {showDescription &&
          cartCustomization.descriptionPosition === 'left' && (
            <p className="text-gray-500 productSubtitleStyle">
              {product.Subtitle}
            </p>
          )}
        {showQuantityButtons && (
          <span
            className="isolate inline-flex rounded-md shadow-sm mt-4"
            style={{ marginTop: showDescription ? '' : '12px' }}>
            <button
              type="button"
              style={{
                color: colorCustomization?.quantityDecreaseIcon,
                backgroundColor: colorCustomization?.quantityDecreaseButton,
              }}
              disabled={
                (product?.Quantity === 1 && isSingleProduct) ||
                isNonRemovable ||
                (product?.Quantity === 1 && isMandatoryProduct)
              }
              onClick={handleDecrease}
              className="relative inline-flex items-center rounded-l-md transition-opacity hover:opacity-80 disabled:opacity-50 px-1 py-1 text-gray-400 ring-1 ring-inset ring-gray-300  focus:z-10">
              <span className="sr-only">Previous</span>
              <MinusIcon
                className="h-6 w-10 lg:h-4 lg:w-6"
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              disabled={isNonRemovable}
              onClick={handleIncrease}
              style={{
                color: colorCustomization?.quantityIncreaseIcon,
                backgroundColor: colorCustomization?.quantityIncreaseButton,
              }}
              className={`relative -ml-px inline-flex items-center rounded-r-md transition-opacity hover:opacity-80 disabled:opacity-50 px-1 py-1 text-gray-400 ring-1 ring-inset ring-gray-300 focus:z-10`}>
              <span className="sr-only">Next</span>
              <PlusIcon className="h-6 w-10 lg:h-4 lg:w-6" aria-hidden="true" />
            </button>
          </span>
        )}
      </div>

      <div
        className={`flex ${productPricePosition === 'left' ? 'flex-row' : 'flex-row-reverse'} gap-4 items-start`}>
        <div>
          {currentSelectedProduct?.totalCartPrice ? (
            <div className="text-editor-content">
              {currentSelectedProduct?.isDisplayPrice ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: currentSelectedProduct?.displayPrice,
                  }}
                />
              ) : (
                <p
                  style={{ color: colorCustomization?.price }}
                  className="flex-none text-sm font-normal mt-0.5 mr-2">
                  {getCurrencyWithSymbol(
                    cartData?.currencyCode || 'USD',
                    // parseFloat(currentSelectedProduct?.totalCartPrice).toFixed(
                    //   2,
                    // ),
                    parseFloat(product.TotalPrice).toFixed(2),
                  )}
                </p>
              )}
            </div>
          ) : (
            <>
              {product.DiscountAmount && product?.DiscountAmount !== '0.00' ? (
                <>
                  <p
                    style={{ color: colorCustomization?.price }}
                    id="discounted-price"
                    className="flex-none text-sm font-normal mr-2 mt-0.5">
                    {getCurrencyWithSymbol(
                      cartData?.currencyCode || 'USD',
                      product?.TotalPrice
                        ? parseFloat(product?.TotalPrice).toFixed(2)
                        : (
                            parseFloat(product?.UnitPrice) * product?.Quantity
                          ).toFixed(2),
                    )}
                  </p>
                  <p
                    className="flex-none text-sm font-normal mr-2"
                    style={{ color: colorCustomization?.price }}>
                    <span
                      style={{ textDecoration: 'line-through', color: 'gray' }}>
                      {getCurrencyWithSymbol(
                        cartData?.currencyCode || 'USD',
                        product?.SubTotalPrice
                          ? String(parseFloat(product?.SubTotalPrice))
                          : String(
                              parseFloat(product?.DiscountAmount) +
                                parseFloat(product?.TotalPrice),
                            ),
                      )}
                    </span>
                  </p>
                </>
              ) : (
                <p
                  className="flex-none text-sm font-normal mt-0.5 mr-2"
                  style={{ color: colorCustomization?.price }}>
                  {getCurrencyWithSymbol(
                    cartData?.currencyCode || 'USD',
                    product?.TotalPrice
                      ? parseFloat(product?.TotalPrice).toFixed(2)
                      : (
                          parseFloat(product?.UnitPrice) * product?.Quantity
                        ).toFixed(2),
                  )}
                </p>
              )}
            </>
          )}
          {showDescription &&
            cartCustomization?.descriptionPosition === 'right' && (
              <p className="text-gray-500 productSubtitleStyle">
                {product.Subtitle}
              </p>
            )}
        </div>
        {!isMandatoryProduct && !cartCustomization?.hideCloseButton && (
          <button
            type="button"
            disabled={isSingleProduct || isNonRemovable || isMandatoryProduct}
            onClick={handleRemove}
            className={`${isSingleProduct || isNonRemovable || isMandatoryProduct ? 'cursor-not-allowed' : ''} removeButton`}>
            <span className="sr-only">Remove</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        )}
      </div>
      <style>
        {`.removeButton {
            color: ${colorCustomization?.productRemove};
          }`}
        {`.productNameStyle {
            color: ${placeholder?.color};
            font-size: ${getFontSize(placeholder?.fontSize)};
            font-weight: ${placeholder?.fontWeight};
            line-height: ${placeholder?.lineHeight};
            text-align: ${placeholder?.textAlign};
            font-family: ${placeholder?.fontFamily};
            letter-spacing: ${placeholder?.letterSpace}px;
          };
            }`}

        {`.productSubtitleStyle {
            color: ${productSubtitle?.color};
            font-size: ${getFontSize(productSubtitle?.fontSize)};   
            font-weight: ${productSubtitle?.fontWeight};
            line-height: ${productSubtitle?.lineHeight};
              
              text-align: ${productSubtitle?.textAlign};
              font-family: ${productSubtitle?.fontFamily};
              letter-spacing: ${productSubtitle?.letterSpace}px;
              }`}
      </style>
    </li>
  );
};
